import React from 'react';
import {
  ChatBubbleBottomCenterIcon,
  BellIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  PaperAirplaneIcon,
  SpeakerWaveIcon,
  EyeIcon,
  ClockIcon,
  ArrowRightIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  WifiIcon,
  ExclamationTriangleIcon,
  CogIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ArrowPathIcon,
  MicrophoneIcon,
} from '@heroicons/react/24/outline';

export default function CommunicationsHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ChatBubbleBottomCenterIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Chat System</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Real-time messaging, notifications, and team communication platform.
            Connect instantly with your team, manage tasks, and stay updated with live alerts.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-8 py-6">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">System Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-200">
                <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                  <BellIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Live Alerts</h3>
                <p className="text-gray-600">
                  Real-time notifications for messages, tasks, and approvals with sound and visual alerts.
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <ChatBubbleBottomCenterIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Team Chat</h3>
                <p className="text-gray-600">
                  Instant messaging with staff status indicators, conversation history, and task integration.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <CogIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Task Management</h3>
                <p className="text-gray-600">
                  Integrated task assignment, approval workflows, and direct links to related systems.
                </p>
              </div>
            </div>

            {/* Communication Flow Diagram */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-6">Communication Flow</h3>
              <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center mb-2">
                    <ChatBubbleBottomCenterIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Send Message</span>
                  <span className="text-xs text-gray-600">Compose & Send</span>
                </div>

                <ArrowRightIcon className="h-6 w-6 text-gray-400 hidden md:block" />
                <div className="md:hidden w-px h-8 bg-gray-300"></div>

                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mb-2">
                    <BellIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Real-time Alert</span>
                  <span className="text-xs text-gray-600">Instant Notification</span>
                </div>

                <ArrowRightIcon className="h-6 w-6 text-gray-400 hidden md:block" />
                <div className="md:hidden w-px h-8 bg-gray-300"></div>

                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mb-2">
                    <EyeIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">View & Respond</span>
                  <span className="text-xs text-gray-600">Chat Interface</span>
                </div>

                <ArrowRightIcon className="h-6 w-6 text-gray-400 hidden md:block" />
                <div className="md:hidden w-px h-8 bg-gray-300"></div>

                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center mb-2">
                    <CheckCircleIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Task Complete</span>
                  <span className="text-xs text-gray-600">Action Taken</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Message Types */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <PaperAirplaneIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Message Types & Actions</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                      <ChatBubbleBottomCenterIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">General Messages</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Direct communication between team members</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Reply functionality with rich text support</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Mark as read/unread status tracking</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Timestamp and sender information</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <ClockIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Overtime Requests</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Accept/reject overtime approval requests</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Bid system for competitive overtime allocation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Direct integration with overtime management</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Automated approval workflow notifications</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mr-3">
                      <UserGroupIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Project Communications</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Project-specific chat notifications</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Direct navigation to project management</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Staff and manager project views</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Context-aware message routing</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-red-600 rounded-lg flex items-center justify-center mr-3">
                      <CogIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Task Integration</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Training course launch buttons</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Admin task assignment links</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Appraisal form navigation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Team task management integration</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step-by-Step Guide */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-8 py-6">
            <div className="flex items-center">
              <ArrowRightIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Complete Communication Workflow</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="space-y-12">
              {/* Step 1: Access Chat Dashboard */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Access Team Communications</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Navigate to the chat dashboard to see all available team members and their current status.
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div className="bg-cyan-50 rounded-xl p-6 border border-cyan-200">
                        <h4 className="text-lg font-semibold text-cyan-900 mb-3">Staff Status Indicators</h4>
                        <ul className="space-y-2 text-cyan-700">
                          <li>• <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span>Online - Currently clocked in</li>
                          <li>• <span className="inline-block w-3 h-3 bg-yellow-500 rounded-full mr-2"></span>Away - On break</li>
                          <li>• <span className="inline-block w-3 h-3 bg-red-500 rounded-full mr-2"></span>Offline - Not clocked in</li>
                          <li>• <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2 animate-pulse"></span>New Message - Unread messages</li>
                        </ul>
                      </div>

                      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                        <h4 className="text-lg font-semibold text-blue-900 mb-3">Navigation Features</h4>
                        <ul className="space-y-2 text-blue-700">
                          <li>• Click any staff member to start chat</li>
                          <li>• Flashing cards indicate new messages</li>
                          <li>• Profile pictures and role information</li>
                          <li>• Real-time status updates via WebSocket</li>
                        </ul>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                        <h4 className="text-lg font-semibold text-indigo-900 mb-3">WebSocket Connection</h4>
                        <ul className="space-y-2 text-indigo-700">
                          <li>• Automatic connection establishment</li>
                          <li>• Real-time message delivery</li>
                          <li>• Connection health monitoring</li>
                          <li>• Automatic reconnection on failure</li>
                        </ul>
                      </div>

                      <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                        <h4 className="text-lg font-semibold text-purple-900 mb-3">Message History</h4>
                        <ul className="space-y-2 text-purple-700">
                          <li>• Complete conversation history</li>
                          <li>• Timestamp tracking</li>
                          <li>• Message status indicators</li>
                          <li>• Chronological message ordering</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2: Send Messages */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Compose & Send Messages</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Use the chat interface to send messages, assign tasks, and communicate with your team.
                  </p>

                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">Message Composition</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <PaperAirplaneIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Multi-line text input with auto-resize</span>
                          </li>
                          <li className="flex items-start">
                            <PaperAirplaneIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Send button with visual feedback</span>
                          </li>
                          <li className="flex items-start">
                            <PaperAirplaneIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Real-time message delivery confirmation</span>
                          </li>
                          <li className="flex items-start">
                            <PaperAirplaneIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Automatic conversation updates</span>
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">Task Integration</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-indigo-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Accept task buttons on messages</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-indigo-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Launch training courses directly</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-indigo-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Navigate to admin tasks</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-indigo-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Access appraisal forms</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3: Receive Alerts */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Receive Real-time Alerts</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Get instant notifications for new messages, tasks, and approvals with customizable alerts.
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-200">
                      <div className="flex items-center mb-6">
                        <BellIcon className="h-8 w-8 text-indigo-500 mr-3" />
                        <h4 className="text-xl font-semibold text-indigo-900">Alert Types</h4>
                      </div>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-indigo-700">General messages with reply options</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-indigo-700">Overtime requests with accept/reject/bid</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-indigo-700">Project notifications with direct navigation</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-indigo-700">Task assignments and approvals</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-200">
                      <div className="flex items-center mb-6">
                        <SpeakerWaveIcon className="h-8 w-8 text-purple-500 mr-3" />
                        <h4 className="text-xl font-semibold text-purple-900">Notification Features</h4>
                      </div>
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 border border-purple-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Desktop Alerts</span>
                            <span className="text-sm text-green-600 font-semibold">Browser API</span>
                          </div>
                          <p className="text-sm text-gray-600">System notifications with custom icons and sounds</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-purple-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Mobile Optimized</span>
                            <span className="text-sm text-blue-600 font-semibold">Touch-Friendly</span>
                          </div>
                          <p className="text-sm text-gray-600">Mobile-specific alert positioning and interactions</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-purple-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Sound Effects</span>
                            <span className="text-sm text-orange-600 font-semibold">Audio Feedback</span>
                          </div>
                          <p className="text-sm text-gray-600">Audible notifications with customizable volume</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4: Respond & Manage */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Respond & Complete Actions</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Take action on messages, complete tasks, and manage your communication workflow efficiently.
                  </p>

                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <ChatBubbleBottomCenterIcon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Reply to Messages</h4>
                        <p className="text-sm text-gray-600">Compose detailed responses with rich formatting</p>
                      </div>

                      <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <CheckCircleIcon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Accept Tasks</h4>
                        <p className="text-sm text-gray-600">One-click task acceptance with status updates</p>
                      </div>

                      <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-orange-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <EyeIcon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Mark Complete</h4>
                        <p className="text-sm text-gray-600">Mark messages as read and track completion</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Features */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <WifiIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Technical Features & Integration</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <WifiIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">WebSocket Real-time</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Persistent WebSocket connections</li>
                  <li>• Automatic reconnection handling</li>
                  <li>• Connection health monitoring</li>
                  <li>• Real-time message delivery</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mr-3">
                    <SpeakerWaveIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Audio Notifications</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Browser notification API</li>
                  <li>• Custom sound effects</li>
                  <li>• Volume controls</li>
                  <li>• Mobile-compatible audio</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-red-600 rounded-lg flex items-center justify-center mr-3">
                    <DevicePhoneMobileIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Cross-Platform</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Desktop and mobile optimization</li>
                  <li>• Touch-friendly interfaces</li>
                  <li>• Responsive design</li>
                  <li>• Platform-specific features</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-600 rounded-lg flex items-center justify-center mr-3">
                    <ShieldCheckIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Security & Privacy</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Secure WebSocket connections</li>
                  <li>• Message encryption</li>
                  <li>• User authentication</li>
                  <li>• Data privacy compliance</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-600 rounded-lg flex items-center justify-center mr-3">
                    <ArrowPathIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Auto-Updates</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Real-time status updates</li>
                  <li>• Live staff availability</li>
                  <li>• Message read receipts</li>
                  <li>• Typing indicators</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-green-50 rounded-2xl p-6 border border-yellow-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-green-600 rounded-lg flex items-center justify-center mr-3">
                    <CogIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Task Integration</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Direct system navigation</li>
                  <li>• Workflow automation</li>
                  <li>• Cross-system linking</li>
                  <li>• Action tracking</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-red-600 to-orange-600 px-8 py-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Troubleshooting & Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <h3 className="text-lg font-bold text-red-900 mb-3">Connection Issues</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">WebSocket Disconnection</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Check internet connection stability</li>
                        <li>• Refresh the page to reconnect</li>
                        <li>• Wait for automatic reconnection</li>
                        <li>• Contact IT if issues persist</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">Messages Not Appearing</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Verify WebSocket connection status</li>
                        <li>• Check browser console for errors</li>
                        <li>• Clear browser cache and cookies</li>
                        <li>• Try a different browser</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                  <h3 className="text-lg font-bold text-yellow-900 mb-3">Notification Problems</h3>
                  <ul className="space-y-2 text-sm text-yellow-700">
                    <li>• Enable browser notifications in settings</li>
                    <li>• Check system sound settings</li>
                    <li>• Allow microphone access for voice features</li>
                    <li>• Update browser to latest version</li>
                    <li>• Disable ad blockers temporarily</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Best Practices</h3>
                  <ul className="space-y-3 text-sm text-blue-700">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Check staff availability before sending urgent messages</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Use appropriate message types for different communications</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Respond promptly to time-sensitive requests</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Mark messages as read after handling</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Use task integration features for workflow efficiency</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-lg font-bold text-green-900 mb-3">Pro Tips</h3>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Use keyboard shortcuts for faster messaging</li>
                    <li>• Monitor staff status for optimal communication timing</li>
                    <li>• Leverage task integration to reduce manual navigation</li>
                    <li>• Keep conversations focused and actionable</li>
                    <li>• Use the system during peak hours for immediate responses</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Usage */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <DevicePhoneMobileIcon className="h-8 w-8 text-white mr-4" />
              <ComputerDesktopIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Platform-Specific Usage</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Mobile Experience</h3>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                    <h4 className="text-lg font-semibold text-blue-900 mb-3">Touch-Optimized Alerts</h4>
                    <ul className="space-y-2 text-blue-700">
                      <li>• Bottom-positioned notifications</li>
                      <li>• Large touch targets for buttons</li>
                      <li>• Swipe gestures for dismissal</li>
                      <li>• Mobile-specific sound handling</li>
                      <li>• Optimized for small screens</li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                    <h4 className="text-lg font-semibold text-indigo-900 mb-3">iOS-Specific Features</h4>
                    <ul className="space-y-2 text-indigo-700">
                      <li>• Safari WebSocket optimization</li>
                      <li>• iOS notification integration</li>
                      <li>• Touch ID/Face ID compatibility</li>
                      <li>• iCloud sync for message history</li>
                      <li>• Background app refresh</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Desktop Experience</h3>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                    <h4 className="text-lg font-semibold text-purple-900 mb-3">Browser Integration</h4>
                    <ul className="space-y-2 text-purple-700">
                      <li>• Browser notification permissions</li>
                      <li>• System tray notifications</li>
                      <li>• Audio context for sound effects</li>
                      <li>• WebSocket persistent connections</li>
                      <li>• Multi-tab synchronization</li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-pink-50 to-red-50 rounded-xl p-6 border border-pink-200">
                    <h4 className="text-lg font-semibold text-pink-900 mb-3">Keyboard Shortcuts</h4>
                    <ul className="space-y-2 text-pink-700">
                      <li>• <kbd className="px-2 py-1 bg-pink-200 rounded text-xs">Enter</kbd> - Send message</li>
                      <li>• <kbd className="px-2 py-1 bg-pink-200 rounded text-xs">Shift+Enter</kbd> - New line</li>
                      <li>• <kbd className="px-2 py-1 bg-pink-200 rounded text-xs">Tab</kbd> - Navigate between fields</li>
                      <li>• <kbd className="px-2 py-1 bg-pink-200 rounded text-xs">Esc</kbd> - Close reply panel</li>
                      <li>• <kbd className="px-2 py-1 bg-pink-200 rounded text-xs">Ctrl+R</kbd> - Refresh conversation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}